import { jsx as _jsx } from "react/jsx-runtime";
/**
 * 翻译历史上下文提供者
 * 提供对翻译历史数据和服务的访问
 */
import { useState, useEffect, useCallback } from 'react';
import { generateCacheKey } from '@readsavor/utils';
import { HistoryContext } from './HistoryContext.js';
import { TranslationHistoryService } from '../services/TranslationHistoryService.js';
import { LoggerService } from '../../logging/services/logger.service.js';
// 获取日志记录器
const logger = LoggerService.getInstance();
/**
 * 翻译历史提供者组件
 * 创建TranslationHistoryService实例，提供上下文和功能
 */
export const HistoryProvider = ({ translationService, children, }) => {
    // 历史服务 - 使用单例模式获取实例
    const [historyService] = useState(() => TranslationHistoryService.getInstance(translationService));
    // 自行管理历史状态
    const [historyState, setHistoryState] = useState({
        totalVocabularyCount: 0,
        isLoading: false,
        lastUpdated: null,
    });
    // 本地维护的缓存，用于实际操作
    const [localCache, setLocalCache] = useState(new Map());
    // 初始化服务
    useEffect(() => {
        const initializeService = async () => {
            try {
                // 加载初始统计信息
                const stats = await historyService.getVocabularyStats();
                setHistoryState(prev => ({
                    ...prev,
                    totalVocabularyCount: stats.totalCount,
                    lastUpdated: Date.now(),
                }));
            }
            catch (error) {
                logger.error('Failed to initialize translation history service', { error });
            }
        };
        initializeService();
    }, [historyService]);
    // 为文档加载翻译
    const loadTranslationsForDocument = useCallback(async (identity) => {
        setHistoryState(prev => ({ ...prev, isLoading: true }));
        try {
            const words = await historyService.loadTranslationsForDocument(identity);
            const wordsDictForDoc = new Map();
            for (const word of words) {
                const key = word.originalText.toLowerCase().trim();
                const existingEntries = wordsDictForDoc.get(key) || [];
                wordsDictForDoc.set(key, [...existingEntries, word]);
            }
            setLocalCache(prevCache => {
                const newCache = new Map(prevCache);
                newCache.set(identity.id.toString(), wordsDictForDoc);
                return newCache;
            });
            setHistoryState(prev => ({
                ...prev,
                isLoading: false,
                lastUpdated: Date.now(),
            }));
            return words;
        }
        catch (error) {
            logger.error('Failed to load translations for document', { error, data: { identity } });
            setHistoryState(prev => ({ ...prev, isLoading: false }));
            return [];
        }
    }, [historyService]);
    // 在缓存中查找翻译
    const findInCache = useCallback((identity, selection) => {
        if (!identity?.id || !selection) {
            return null;
        }
        try {
            const docCache = localCache.get(identity.id.toString());
            if (!docCache)
                return null;
            // 生成唯一的业务 ID
            const keyForExactMatch = generateCacheKey(selection, identity);
            // 优化查找：先通过原文文本缩小范围
            const keyForCandidates = selection.selectedText.toLowerCase().trim();
            const candidates = docCache.get(keyForCandidates);
            if (candidates && candidates.length > 0) {
                // 在候选项中通过唯一的业务 ID 精确查找
                const exact = candidates.find(candidate => candidate.id === keyForExactMatch);
                if (exact) {
                    return exact;
                }
            }
            return null;
        }
        catch (error) {
            logger.error('Failed to find translation in cache', { error });
            return null;
        }
    }, [localCache]);
    // 获取翻译记录 (从数据库)
    const getTranslation = useCallback(async (identity, selection) => {
        if (!identity || !selection) {
            return null;
        }
        try {
            return await historyService.getTranslation(identity, selection);
        }
        catch (error) {
            logger.error('Failed to get translation record', { error, data: { identity, selection } });
            return null;
        }
    }, [historyService]);
    // 刷新统计信息
    const refreshStats = useCallback(async () => {
        try {
            const stats = await historyService.getVocabularyStats();
            setHistoryState(prev => {
                const newState = {
                    ...prev,
                    totalVocabularyCount: stats.totalCount,
                    lastUpdated: Date.now(),
                };
                return newState;
            });
        }
        catch (error) {
            logger.error('Failed to refresh stats', { error });
        }
    }, [historyService]);
    // 保存翻译
    const saveTranslation = useCallback(async (translationData) => {
        try {
            // Generate the cache key directly using the same logic as storage service
            const selectionData = {
                type: translationData.location.type,
                selectedText: translationData.originalText,
                location: translationData.location.type === 'text'
                    ? {
                        paragraphData: { id: translationData.location.paragraphId },
                        startOffset: translationData.location.startOffset,
                        endOffset: translationData.location.endOffset,
                    }
                    : {
                        paragraphId: translationData.location.paragraphId,
                        startWordId: translationData.location.startWordId,
                        endWordId: translationData.location.endWordId,
                        startWordOffset: translationData.location.startWordOffset,
                        endWordOffset: translationData.location.endWordOffset,
                        selectionRects: translationData.location.rects || [],
                    },
            };
            const generatedId = generateCacheKey(selectionData, translationData.documentIdentity);
            // Create the complete record with correct ID
            const recordWithId = {
                ...translationData,
                id: generatedId,
                timestamp: Date.now(),
            };
            const orderId = await historyService.saveTranslation(recordWithId);
            // Create the final translation object with orderId
            const fullTranslation = {
                ...recordWithId,
                orderId,
            };
            // Step 4: Update local cache with the full object.
            if (fullTranslation.documentIdentity && fullTranslation.documentIdentity.id !== undefined) {
                setLocalCache(prevCache => {
                    const newCache = new Map(prevCache);
                    const docIdStr = fullTranslation.documentIdentity.id.toString();
                    const docCache = new Map(newCache.get(docIdStr) || []);
                    const key = fullTranslation.originalText.toLowerCase().trim();
                    const existingEntries = docCache.get(key) || [];
                    // The comparison is now string vs string, which is correct.
                    const updatedEntries = existingEntries.filter(entry => entry.id !== fullTranslation.id);
                    updatedEntries.push(fullTranslation);
                    docCache.set(key, updatedEntries);
                    newCache.set(docIdStr, docCache);
                    return newCache;
                });
            }
            refreshStats();
            return fullTranslation;
        }
        catch (error) {
            logger.error('Failed to save translation', { error, data: { translationData } });
            return null;
        }
    }, [historyService, refreshStats]);
    // 搜索词汇
    const searchVocabulary = useCallback(async (searchText, options) => {
        try {
            return await historyService.searchVocabulary(searchText, options);
        }
        catch (error) {
            logger.error('Failed to search vocabulary', { error });
            return [];
        }
    }, [historyService]);
    // 删除词汇项
    const deleteVocabularyItem = useCallback(async (id) => {
        try {
            const result = await historyService.deleteVocabularyItem(id);
            if (result) {
                setLocalCache(prevCache => {
                    const newCache = new Map(prevCache);
                    newCache.forEach((docCache, docIdStr) => {
                        let docCacheAltered = false;
                        const updatedDocCacheEntries = [];
                        docCache.forEach((translations, key) => {
                            const filteredTranslations = translations.filter(t => t.orderId !== id);
                            if (filteredTranslations.length < translations.length) {
                                docCacheAltered = true;
                                if (filteredTranslations.length > 0) {
                                    updatedDocCacheEntries.push([key, filteredTranslations]);
                                }
                            }
                            else {
                                updatedDocCacheEntries.push([key, translations]);
                            }
                        });
                        if (docCacheAltered) {
                            if (updatedDocCacheEntries.length > 0) {
                                newCache.set(docIdStr, new Map(updatedDocCacheEntries));
                            }
                            else {
                                newCache.delete(docIdStr);
                            }
                        }
                    });
                    return newCache;
                });
                refreshStats();
                // historyState.translatedWords no longer exists for direct filtering
                setHistoryState(prev => ({ ...prev, lastUpdated: Date.now() }));
            }
            return result;
        }
        catch (error) {
            logger.error('Failed to delete vocabulary item', { error });
            return false;
        }
    }, [historyService, refreshStats]);
    // 获取所有词汇
    const getAllVocabulary = useCallback(async (options) => {
        try {
            return await historyService.getAllVocabulary(options);
        }
        catch (error) {
            logger.error('Failed to get all vocabulary', { error });
            return [];
        }
    }, [historyService]);
    // 清除当前文档的缓存
    const clearCurrentDocumentCache = useCallback((identity) => {
        if (identity && identity.id !== undefined) {
            setLocalCache(prevCache => {
                const newCache = new Map(prevCache);
                newCache.delete(identity.id.toString());
                return newCache;
            });
            logger.debug('Translation cache for the current document has been cleared', {
                data: { identity },
            });
        }
        else {
            // 如果没有提供 documentId，可以选择清除所有文档的缓存或记录警告
            // 当前行为：不清除任何内容，仅记录
            // 或者可以清除整个 localCache: setLocalCache(new Map());
            logger.warn('clearCurrentDocumentCache called without specific document identity; no cache cleared.');
        }
    }, []);
    // 创建上下文值
    const contextValue = {
        historyState, // Uses the new CoreTranslationHistoryState structure
        loadTranslationsForDocument,
        findInCache,
        saveTranslation,
        searchVocabulary,
        deleteVocabularyItem,
        getAllVocabulary,
        refreshStats,
        clearCurrentDocumentCache: clearCurrentDocumentCache, // Ensure this matches the updated signature in ITranslationHistoryContext
        getTranslation,
    };
    return _jsx(HistoryContext.Provider, { value: contextValue, children: children });
};
export default HistoryProvider;
//# sourceMappingURL=HistoryProvider.js.map